import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Button,
  FileButton,
  Box,
  Group,
  Menu,
  Popover,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconNote, IconTrash } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { countryChineseName } from "../helpers/countryNames";
import { useServerApi } from "../hooks/userServerApi";
import { saveBlobDownload } from "../utils/fileDownload";
import {
  applyAutoMatchDecisions,
  canDownloadMatchReportFile,
  commentSummary,
  discountFactorSelectData,
  matchReportFileFromUpload,
  matchReportNote,
  normalizeMatchDecision,
  screeningHandlerProfile,
} from "../helpers/nameScreening";

const attachClientFile = async (form, stored) => {
  if (!stored || typeof form?.setFieldValue !== "function") return;
  const current = Array.isArray(form.values?.files) ? form.values.files : [];
  const id = stored._id == null ? "" : String(stored._id);
  const link = String(stored.link || stored.path || stored.url || "");
  const already = current.some((file) => {
    if (id && String(file?._id) === id) return true;
    const fileLink = String(file?.link || file?.path || file?.url || "");
    return Boolean(link) && fileLink === link;
  });
  if (already) return;
  const next = [...current, stored];
  form.setFieldValue("files", next);
  const clientId = form.values?._id || form.values?.id;
  if (clientId && typeof form.savePartialFields === "function") {
    await form.savePartialFields({ files: next });
  }
};

const toDownloadFile = (file) => ({
  ...file,
  link: file?.link ?? file?.path ?? "",
  name: file?.name ?? file?.filename,
  filename: file?.filename ?? file?.name,
  mimetype: file?.mimetype ?? file?.mimeType,
});

const ValueWithScore = ({ value, score, title, wrap = false }) => {
  const primary = text(value);
  const secondary = text(score);
  if (!primary && !secondary) return null;
  return (
    <Stack spacing={0} sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Text
        size="xs"
        title={title || primary}
        sx={{
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "unset" : "ellipsis",
          whiteSpace: wrap ? "normal" : "nowrap",
          wordBreak: wrap ? "break-word" : "normal",
        }}
      >
        {primary || "—"}
      </Text>
      {secondary ? (
        <Text
          size="10px"
          color="dimmed"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {secondary}
        </Text>
      ) : null}
    </Stack>
  );
};

const text = (value) => String(value ?? "").trim();

const MATCH_STATUS = [
  { value: "PENDING", label: "Pending/Not reviewed", color: "yellow" },
  { value: "MATCHED", label: "Matched", color: "red" },
  { value: "NOT_MATCHED", label: "Not matched", color: "green" },
  { value: "UNSURE", label: "Unsure", color: "blue" },
];

const dateValue = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatUpdateDate = (value) => {
  const date = dateValue(value);
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const HitFiles = ({ files, clientId, onChange }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [uploading, setUploading] = useState(false);
  const list = Array.isArray(files) ? files : [];

  const upload = async (incoming) => {
    const next = [...list];
    setUploading(true);
    try {
      for (const file of incoming || []) {
        if (!file) continue;
        const result = await api.uploadFile("files", file, clientId || null, next, {
          entityType: "client",
          entityField: "dueDiligence.nameScreening",
        });
        if (result?.success && result.data) {
          next.push(result.data);
        } else {
          showNotification({ color: "red", message: t("Upload failed") });
        }
      }
      onChange(next);
    } catch (error) {
      showNotification({ color: "red", message: t("Upload failed") });
    }
    setUploading(false);
  };

  return (
    <Stack spacing={6}>
      <Dropzone
        onDrop={upload}
        onReject={() => showNotification({ color: "red", message: t("Upload failed") })}
        accept={[...IMAGE_MIME_TYPE, "application/pdf"]}
        radius="sm"
        p="sm"
        loading={uploading}
        styles={{ root: { borderStyle: "dashed" } }}
      >
        <Text size="sm" color="dimmed" align="center">
          {t("Drop PDF or image, or click to browse")}
        </Text>
      </Dropzone>
      {list.map((file, index) => (
        <Group key={file?._id || file?.link || file?.name || index} position="apart" noWrap spacing="xs">
          <Text size="xs" lineClamp={1} sx={{ flex: 1, minWidth: 0 }}>
            {file?.name || file?.filename}
          </Text>
          <ActionIcon
            color="red"
            variant="subtle"
            size="sm"
            aria-label={t("Remove")}
            onClick={() => onChange(list.filter((_, fileIndex) => fileIndex !== index))}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}
    </Stack>
  );
};

const HitComment = ({ hit, clientId, onChange }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const summary = commentSummary(hit);
  const filled = Boolean(summary);
  const otherValue = hit.otherDiscountFactor || hit.remark || "";
  const options = discountFactorSelectData(t);

  const setField = (field, value) => {
    const patch = { [field]: value || "" };
    if (field === "otherDiscountFactor") patch.remark = "";
    onChange(patch);
  };

  return (
    <Box sx={{ position: "relative", width: 28 }}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-end"
        withinPortal
        shadow="md"
        width={480}
      >
        <Popover.Target>
            <ActionIcon
              size="sm"
              variant={filled ? "light" : "subtle"}
              color={filled ? "green" : "gray"}
              aria-label={t("Comment")}
              onClick={() => setOpened((current) => !current)}
            >
              <IconNote size={16} />
            </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack spacing="sm">
            <div>
              <Text size="sm" weight={600}>
                {(hit.names || []).filter(Boolean).join(", ") || t("Comment")}
              </Text>
              <Text size="xs" color="dimmed">
                {[hit.matchListName, hit.mainSrcRefId].filter(Boolean).join(" · ")}
              </Text>
            </div>
            <Stack spacing={2}>
              <Text size="xs" color="dimmed">
                {t("Strong primary factor (1 is required to discount an alert)")}
              </Text>
              <Text size="xs" color="dimmed">
                {t("Supporting discount factor (2 are required to discount an alert)")}
              </Text>
            </Stack>
            <SimpleGrid cols={2} spacing="sm">
              <Select
                size="xs"
                clearable
                searchable
                label={t("Discount Factor 1")}
                data={options}
                value={hit.discountFactor1 || null}
                onChange={(value) => setField("discountFactor1", value)}
              />
              <Select
                size="xs"
                clearable
                searchable
                label={t("Discount Factor 2")}
                data={options}
                value={hit.discountFactor2 || null}
                onChange={(value) => setField("discountFactor2", value)}
              />
            </SimpleGrid>
            <Textarea
              size="xs"
              minRows={2}
              label={t("Other Discount Factor")}
              value={otherValue}
              onChange={(event) =>
                setField("otherDiscountFactor", event.currentTarget.value)
              }
            />
            <DatePicker
              size="xs"
              label={t("Update Date")}
              placeholder={t("Pick Date")}
              allowFreeInput
              withinPortal
              zIndex={400}
              value={dateValue(hit.updateDate)}
              onChange={(value) =>
                onChange({ updateDate: value ? value.toISOString() : "" })
              }
            />
            <HitFiles
              files={hit.files}
              clientId={clientId}
              onChange={(updatedFiles) => onChange({ files: updatedFiles })}
            />
            <Button size="xs" onClick={() => setOpened(false)} sx={{ alignSelf: "flex-end" }}>
              {t("Done")}
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {filled ? (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 1,
            right: 1,
            width: 6,
            height: 6,
            borderRadius: 6,
            background: theme.colors.green[6],
            pointerEvents: "none",
          })}
        />
      ) : null}
    </Box>
  );
};

const DecisionControl = ({ decision, onChange }) => {
  const { t } = useTranslation();
  const current =
    MATCH_STATUS.find((status) => status.value === normalizeMatchDecision(decision)) ||
    MATCH_STATUS[0];

  return (
    <Menu shadow="md" withinPortal position="bottom-end" width={210}>
      <Menu.Target>
        <ActionIcon
          size="sm"
          variant="light"
          color={current.color}
          aria-label={t(current.label)}
        >
          <Box
            sx={(theme) => ({
              width: 10,
              height: 10,
              borderRadius: 10,
              background: theme.colors[current.color][6],
            })}
          />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {MATCH_STATUS.map((status) => (
          <Menu.Item
            key={status.value}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChange(status.value);
            }}
          >
            <Group spacing={6} noWrap>
              <Box
                sx={(theme) => ({
                  width: 8,
                  height: 8,
                  borderRadius: 8,
                  background: theme.colors[status.color][6],
                  flexShrink: 0,
                })}
              />
              <Text size="xs">{t(status.label)}</Text>
            </Group>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

export const NameScreeningHitTable = ({
  hits,
  clientId,
  onHitChange,
  highlightPending = false,
}) => {
  const { t } = useTranslation();
  const list = Array.isArray(hits) ? hits : [];
  if (!list.length) return null;

  return (
    <Stack spacing="md">
      <Group spacing="md">
        {MATCH_STATUS.map((status) => {
          const count = list.filter(
            (hit) => normalizeMatchDecision(hit.decision) === status.value
          ).length;
          return (
            <Group key={status.value} spacing={6}>
              <Box
                sx={(theme) => ({
                  width: 10,
                  height: 10,
                  borderRadius: 10,
                  background: theme.colors[status.color][6],
                })}
              />
              <Text size="xs">{`${t(status.label)} (${count})`}</Text>
            </Group>
          );
        })}
      </Group>
      <Box sx={{ maxHeight: 360, overflow: "auto", overflowX: "auto" }}>
        <Table
          fontSize="xs"
          verticalSpacing="xs"
          highlightOnHover
          sx={(theme) => ({
            tableLayout: "fixed",
            minWidth: 860,
            "thead th": {
              position: "sticky",
              top: 0,
              zIndex: 1,
              background:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              whiteSpace: "normal",
            },
            "tbody td": { verticalAlign: "top" },
            "td[data-paired]": { overflow: "hidden" },
            "td[data-name]": { whiteSpace: "normal", wordBreak: "break-word" },
            "td[data-comment-line]": { overflow: "visible", whiteSpace: "normal" },
          })}
        >
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t("Match list")}</th>
              <th style={{ width: 180 }} title={t("Name score")}>{t("Name")}</th>
              <th style={{ width: 128 }}>{t("Update Date")}</th>
              <th style={{ width: 88 }} title={t("Country score")}>{t("Country")}</th>
              <th style={{ width: 96 }} title={t("Birth date score")}>{t("Birth date")}</th>
              <th style={{ width: 72 }}>{t("Overall")}</th>
              <th style={{ width: 88 }}>{t("Decision")}</th>
              <th style={{ width: 88 }}>{t("Comment")}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((hit, index) => {
              const names = (hit.names || []).filter(Boolean).join(", ");
              const summary = commentSummary(hit);
              const pending =
                highlightPending && normalizeMatchDecision(hit.decision) === "PENDING";
              return (
                <React.Fragment key={`${hit.matchListId}-${hit.mainSrcRefId}-${index}`}>
                  <tr style={pending ? { background: "rgba(250, 176, 5, 0.16)" } : undefined}>
                    <td>
                      <Stack spacing={0}>
                        <Text size="xs">{hit.matchListName}</Text>
                        {hit.mainSrcRefId ? (
                          <Text size="xs" color="dimmed">{hit.mainSrcRefId}</Text>
                        ) : null}
                      </Stack>
                    </td>
                    <td data-name>
                      <ValueWithScore wrap value={names} score={hit.nameScore} title={names} />
                    </td>
                    <td>
                      <Text size="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatUpdateDate(hit.updateDate) || "—"}
                      </Text>
                    </td>
                    <td data-paired>
                      <ValueWithScore value={hit.country} score={hit.countryScore} />
                    </td>
                    <td data-paired>
                      <ValueWithScore value={hit.birthDate} score={hit.birthDateScore} />
                    </td>
                    <td>
                      <Text size="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {hit.overallScore || "—"}
                      </Text>
                    </td>
                    <td data-decision>
                      <DecisionControl
                        decision={hit.decision}
                        onChange={(value) => onHitChange(index, { decision: value })}
                      />
                    </td>
                    <td data-comment>
                      <HitComment
                        hit={hit}
                        clientId={clientId}
                        onChange={(patch) => onHitChange(index, patch)}
                      />
                    </td>
                  </tr>
                  {summary ? (
                    <tr>
                      <td colSpan={8} data-comment-line style={{ borderTop: "none", paddingTop: 0 }}>
                        <Text size="xs" color="dimmed">{summary}</Text>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Box>
    </Stack>
  );
};

const ClientDueDiligenceMatchReport = ({
  report,
  client,
  clientName,
  clientId,
  subjectLabel,
  alertId,
  logId,
  screenedAt,
  onChange,
  view = "all",
  toolbar = null,
  resolvedAt = "",
  form,
  highlightPending = false,
}) => {
  const { t, i18n } = useTranslation();
  const [api] = useServerApi();
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [localReport, setLocalReport] = useState(report);
  const localFileRef = useRef(null);
  useEffect(() => {
    setLocalReport(report);
    if (canDownloadMatchReportFile(report)) {
      localFileRef.current = null;
    }
  }, [report]);
  const hits = localReport?.hits || [];
  const screeningLabel = subjectLabel || clientName || "";

  const runAutoDecisions = (reportHits, { onlyPending = true } = {}) =>
    applyAutoMatchDecisions(client || {}, reportHits, {
      onlyPending,
      subjectLabel: screeningLabel,
    });

  const applyReport = (patch) => {
    const next = { ...(localReport || {}), ...patch };
    setLocalReport(next);
    onChange(next);
  };

  const autoDecide = () => {
    const nextHits = runAutoDecisions(hits, { onlyPending: false });
    const changed = nextHits.some(
      (hit, index) => hit.decision !== hits[index]?.decision
    );
    applyReport({ hits: nextHits });
    if (changed) {
      showNotification({
        color: "green",
        message: t("Obvious non-matches were marked automatically"),
      });
    } else {
      showNotification({
        color: "blue",
        message: t("No obvious non-matches to mark"),
      });
    }
  };

  const note = useMemo(
    () =>
      matchReportNote({
        clientName,
        alertId,
        logId,
        screenedAt,
        report: localReport,
      }),
    [clientName, alertId, logId, screenedAt, localReport]
  );

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await api.FwdAml.parseMatchReport(file);
      let storedFile = null;
      const uploadResult = await api.uploadFile(
        "files",
        file,
        clientId || null,
        [],
        { entityType: "client", entityField: "dueDiligence.nameScreening" }
      );
      if (uploadResult?.data) {
        storedFile = matchReportFileFromUpload(uploadResult.data, file.name);
        try {
          await attachClientFile(form, uploadResult.data);
        } catch (attachError) {
          showNotification({
            color: "orange",
            message: t("The PDF was saved on this screen but not on the client files"),
          });
        }
      }
      if (canDownloadMatchReportFile({ file: storedFile })) {
        localFileRef.current = null;
      } else {
        localFileRef.current = file;
        showNotification({
          color: "orange",
          message: t("The PDF was parsed but could not be stored for download"),
        });
      }
      const next = {
        uid: data?.uid || "",
        screenedName: data?.screenedName || "",
        fileName: file.name,
        ...(storedFile ? { file: storedFile } : {}),
        hits: runAutoDecisions(data?.hits || []),
      };
      applyReport(next);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Name screening failed"),
        message: t("This PDF is not an FCRM online check result"),
      });
    }
    setUploading(false);
  };

  const download = async () => {
    const fileName = localReport?.fileName || "match-report.pdf";
    if (localFileRef.current) {
      saveBlobDownload(localFileRef.current, fileName);
      return;
    }
    const file = localReport?.file;
    if (!canDownloadMatchReportFile(localReport)) return;
    setDownloading(true);
    try {
      const result = await api.downloadFiles(
        [toDownloadFile(file)],
        file.filename || file.name || fileName
      );
      saveBlobDownload(result.blob, result.filename || fileName);
    } catch (error) {
      showNotification({
        color: "red",
        message: t("Download failed"),
      });
    }
    setDownloading(false);
  };

  const updateHit = (index, patch) => {
    const next = {
      ...(localReport || {}),
      hits: hits.map((hit, hitIndex) =>
        hitIndex === index ? { ...hit, ...patch } : hit
      ),
    };
    setLocalReport(next);
    onChange(next);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(note);
      showNotification({
        color: "green",
        message: t("Result copied"),
      });
    } catch (error) {
      showNotification({
        color: "orange",
        message: t("Select the result below and copy it"),
      });
    }
  };

  const profile = screeningHandlerProfile(client, screeningLabel);
  const showChineseCountry = String(i18n?.language || "")
    .toLowerCase()
    .startsWith("zh");
  const residentialCountry = showChineseCountry
    ? countryChineseName(profile.residentialCountry) || profile.residentialCountry
    : profile.residentialCountry;
  const clientFacts = [
    [t("English name"), profile.englishName],
    [t("Chinese name"), profile.chineseName],
    [t("Date of birth"), profile.dateOfBirth],
    [t("Residential country"), residentialCountry],
  ];
  const showMatching = view === "all" || view === "matching";
  const showResult = view === "all" || view === "result";
  if (!showMatching && !showResult) return null;

  const fileName = localReport?.fileName;
  const fileIsDownloadable = canDownloadMatchReportFile(
    localReport,
    Boolean(localFileRef.current)
  );

  const fileLabel = fileName ? (
    fileIsDownloadable ? (
      <Anchor
        component="button"
        type="button"
        size="sm"
        onClick={download}
        sx={{
          maxWidth: 420,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: downloading ? "wait" : "pointer",
        }}
      >
        {fileName}
      </Anchor>
    ) : (
      <Text size="sm" color="dimmed">
        {fileName}
      </Text>
    )
  ) : null;

  return (
    <Stack spacing="lg" mt="lg">
      {showMatching ? (
      <Stack spacing="lg">
      <Group spacing="md" align="flex-end">
        {toolbar}
        <FileButton onChange={upload} accept="application/pdf,.pdf">
          {(props) => (
            <Button {...props} size="sm" variant="outline" loading={uploading}>
              {t("Upload result PDF")}
            </Button>
          )}
        </FileButton>
        {hits.length ? (
          <Tooltip label={t("Auto mark obvious non-matches")} withArrow openDelay={300}>
            <Button size="sm" variant="outline" onClick={autoDecide}>
              {t("Auto")}
            </Button>
          </Tooltip>
        ) : null}
      </Group>
      <SimpleGrid cols={4} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 2 }]}>
        {clientFacts.map(([label, value]) => (
          <Box
            key={label}
            p="sm"
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
              borderRadius: theme.radius.sm,
            })}
          >
            <Text size="xs" color="dimmed">
              {label}
            </Text>
            <Text size="sm" mt={6}>
              {value || "—"}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
      {hits.length ? (
        <NameScreeningHitTable
          hits={hits}
          clientId={clientId}
          highlightPending={highlightPending}
          onHitChange={updateHit}
        />
      ) : null}
      {fileLabel || resolvedAt ? (
        <Group spacing="lg" align="center">
          {fileLabel}
          {resolvedAt ? (
            <Text size="sm" color="dimmed">
              {`${t("Resolved at")}: ${resolvedAt}`}
            </Text>
          ) : null}
        </Group>
      ) : null}
      </Stack>
      ) : null}
      {showResult ? (
        hits.length ? (
          <Stack spacing="lg">
            <Textarea value={note} readOnly minRows={6} />
            <Button size="sm" variant="outline" onClick={copy} sx={{ alignSelf: "flex-start" }}>
              {t("Copy result")}
            </Button>
          </Stack>
        ) : (
          <Text size="sm" color="dimmed">
            {t("Upload a result PDF to build the note")}
          </Text>
        )
      ) : null}
    </Stack>
  );
};

export default ClientDueDiligenceMatchReport;
